import React, { useMemo } from 'react';
import { DataTable } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { Action } from '@aidkitorg/component-library/src/ActionsDropdown';
import { useLocalizedStrings } from '../../../Localization';

export interface ApplicantGroup {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApplicantGroupsDataTableProps {
  groups: ApplicantGroup[];
  onViewResults: (group: ApplicantGroup) => void;
  onEdit: (group: ApplicantGroup) => void;
  onDelete: (group: ApplicantGroup) => void;
  isLoading?: boolean;
  pageConfig?: any;
  isConfigLoading?: boolean;
}

export const ApplicantGroupsDataTable: React.FC<ApplicantGroupsDataTableProps> = ({
  groups,
  onViewResults,
  onEdit,
  onDelete,
  isLoading = false,
  pageConfig,
}) => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;

  // Use config to determine visibility
  const showDescription = pageConfig?.defaultTableColumns?.description === 'visible';
  const showMembers = pageConfig?.defaultTableColumns?.members === 'visible';
  const showSearchBar = pageConfig?.searchBar === 'visible';

  // Check if any actions should be visible
  const actionsConfig = pageConfig?.defaultTableColumns?.actionsDropdown;
  const showViewAction = actionsConfig?.view === 'visible';
  const showEditAction = actionsConfig?.edit === 'visible';
  const showDeleteAction = actionsConfig?.delete === 'visible';
  const showActionsColumn = showViewAction || showEditAction || showDeleteAction;

  // Define actions for the hamburger menu - only include visible actions
  const actions: Action<ApplicantGroup>[] = useMemo(() => {
    const availableActions: Action<ApplicantGroup>[] = [];

    if (showViewAction) {
      availableActions.push({
        label: strings.view_full_results,
        onClick: onViewResults,
        icon: 'EyeIcon',
      });
    }

    if (showEditAction) {
      availableActions.push({
        label: strings.edit,
        onClick: onEdit,
        icon: 'PencilIcon',
      });
    }

    if (showDeleteAction) {
      availableActions.push({
        label: strings.delete,
        onClick: onDelete,
        icon: 'TrashIcon',
        variant: 'warn',
      });
    }

    return availableActions;
  }, [strings, onViewResults, onEdit, onDelete, showViewAction, showEditAction, showDeleteAction]);

  // Define columns for the data table - conditionally include based on config
  const columns = useMemo<ColumnDef<ApplicantGroup>[]>(() => {
    const cols: ColumnDef<ApplicantGroup>[] = [];

    // Name column is always visible
    cols.push({
      accessorKey: 'name',
      header: strings.group_name,
      enableSorting: true,
      cell: ({ getValue }) => (
        <div className="font-medium text-gray-900">
          {getValue() as string}
        </div>
      ),
    });

    // Description column - conditional
    if (showDescription) {
      cols.push({
        accessorKey: 'description',
        header: strings.group_description,
        enableSorting: false,
        cell: ({ getValue }) => {
          const description = getValue() as string;
          return (
            <div className="text-gray-600 truncate max-w-md">
              {description || '—'}
            </div>
          );
        },
      });
    }

    // Members column - conditional
    if (showMembers) {
      cols.push({
        accessorKey: 'memberCount',
        header: strings.members,
        enableSorting: true,
        cell: ({ getValue }) => {
          const count = getValue() as number;
          return (
            <div className="text-gray-900">
              {count.toLocaleString()}
            </div>
          );
        },
      });
    }

    return cols;
  }, [strings, showDescription, showMembers]);

  return (
    <DataTable
      columns={columns}
      data={groups}
      actions={showActionsColumn ? actions : undefined}
      enableSearch={showSearchBar}
      searchLabel={strings.search_groups}
      pageSize={10}
      showPagination={true}
      initializing={isLoading}
      getRowId={(row) => row.id}
    />
  );
};

export default ApplicantGroupsDataTable;
